import React, { useEffect } from 'react';
import { Price } from 'src/components/Helpers/Price';
import { useQty } from 'src/components/ProductDetail/ProductDetailContext';
import { useVatCausesRender } from 'src/components/AppShell/VatProvider';
import { ExpandCollapseWrapper } from 'src/components/ExpandCollapseWrapper/ExpandCollapseWrapper';

import classes from './TieredPricing.scss';

export interface TierPrice {
    qty: number;
    price: number;
    price_incl: number;
    discount: number;
}

type Props = {
    tieredPricing: TierPrice[];
    priceChanged?: (price: TierPrice) => void;
    isCallUsTierEnabled?: boolean;
};

export const TieredPricing: React.FC<Props> = ({ tieredPricing, priceChanged, isCallUsTierEnabled }) => {
    const qtySelected = useQty();
    const { vat } = useVatCausesRender();
    const priceTiers = !isCallUsTierEnabled
        ? tieredPricing
        : [
              ...tieredPricing,
              {
                  price: 'Call Us',
                  qty: tieredPricing.splice(-1)[0].qty * 2,
                  discount: 0,
                  price_incl: '',
              },
          ];

    useEffect(() => {
        const selectedPrice = tieredPricing.filter((price, index) => {
            const next = tieredPricing[index + 1];
            const nextQty = (next && next.qty) ?? Infinity;
            return qtySelected >= price.qty && qtySelected < nextQty;
        })[0];
        priceChanged && priceChanged(selectedPrice || tieredPricing[0]);
    }, [qtySelected, priceChanged, tieredPricing]);

    return (
        <ExpandCollapseWrapper
            turnOff={true}
            itemHeight={48}
            itemsAmountCollapsed={3}
            itemsAmountExpanded={tieredPricing.length}
        >
            <div className={classes.tieredList}>
                {priceTiers.map((price, index) => {
                    const isSingle = price.qty === 1;
                    const next = tieredPricing[index + 1];
                    const nextQty = (next && next.qty) ?? Infinity;
                    const isActive = qtySelected >= price.qty && qtySelected < nextQty;
                    const state = (() => {
                        if (qtySelected === 0) return State.Idle;
                        if (isActive) return State.Active;
                        return State.Inactive;
                    })();

                    return (
                        <div key={index} className={classes.tieredItem} data-value={state}>
                            {typeof price.price === 'number' ? (
                                <Price
                                    price={price.price}
                                    priceIncl={price.price_incl}
                                    vat={vat}
                                    className={classes.tieredPrice}
                                />
                            ) : (
                                <div className={classes.tieredPrice}> Call Us </div>
                            )}

                            <div>
                                {!isSingle && price.discount > 0 && (
                                    <small className={classes.tieredDiscount}>save {Math.abs(price.discount)}%</small>
                                )}
                            </div>
                            <span className={classes.itemQty}>
                                {price.qty}
                                {!isSingle && '+'} item{!isSingle && 's'}
                            </span>
                        </div>
                    );
                })}
            </div>
        </ExpandCollapseWrapper>
    );
};

enum State {
    Idle = 'Idle',
    Active = 'Active',
    Inactive = 'Inactive',
}
